import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [billingCycle, setBillingCycle] = useState(searchParams.get("billing") || "monthly");
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(searchParams.get("token") || null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [monthlyPlan, setMonthlyPlan] = useState(null);
  const [yearlyPlan, setYearlyPlan] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const { user } = useAuth();
  const isYearly = billingCycle === "yearly";
  const selectedPlan = isYearly ? yearlyPlan : monthlyPlan;
  // Context is authoritative; fall back to URL param set by AnalystUpgradePage
  const isTrial = user?.trial_eligible ?? (searchParams.get('plan_type') === 'trial');

  // Protect against null/undefined prices and non-numeric values
  const basePrice = isYearly
    ? parseFloat(yearlyPlan?.price ?? 0)
    : parseFloat(monthlyPlan?.price ?? 0);
  const safeBase = isNaN(basePrice) ? 0 : basePrice;
  const finalPrice = Math.max(0, safeBase - (Number(discount) || 0));

  useEffect(() => {
    const fetchPlans = async () => {
      const monthlyId = searchParams.get("monthly_id");
      const yearlyId = searchParams.get("yearly_id");

      if (!monthlyId && !yearlyId) {
        setIsLoadingPlans(false);
        return;
      }

      setIsLoadingPlans(true);

      try {
        const fetchPromises = [];

        if (monthlyId) {
          fetchPromises.push(
            fetch(`${API_BASE_URL}/api/payments/subscription/plans/${monthlyId}/`)
              .then((res) => {
                if (res.ok) {
                  return res.json();
                }
                throw new Error(`Failed to fetch monthly plan: ${res.status}`);
              })
              .then((data) => setMonthlyPlan(data))
              .catch((error) => {
                console.error("Error fetching monthly plan:", error);
                setError(`Failed to load monthly plan: ${error.message}`);
              })
          );
        }

        if (yearlyId) {
          fetchPromises.push(
            fetch(`${API_BASE_URL}/api/payments/subscription/plans/${yearlyId}/`)
              .then((res) => {
                if (res.ok) {
                  return res.json();
                }
                throw new Error(`Failed to fetch yearly plan: ${res.status}`);
              })
              .then((data) => setYearlyPlan(data))
              .catch((error) => {
                console.error("Error fetching yearly plan:", error);
                setError(`Failed to load yearly plan: ${error.message}`);
              })
          );
        }

        await Promise.all(fetchPromises);
      } catch (error) {
        console.error("Error fetching plans:", error);
        setError("Failed to load plan details. Please try again.");
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []); // Only run once on mount

  // Update selection when billing cycle OR plans change
  useEffect(() => {
    let newPlanId = null;
    if (billingCycle === "yearly" && yearlyPlan?.id) {
      newPlanId = yearlyPlan.id;
    } else if (billingCycle === "monthly" && monthlyPlan?.id) {
      newPlanId = monthlyPlan.id;
    }
    setSelectedPlanId(newPlanId);
  }, [billingCycle, monthlyPlan, yearlyPlan]);


  useEffect(() => {
    const verifyToken = async () => {
      const tokenToVerify = searchParams.get("token") || localStorage.getItem("token");

      if (!tokenToVerify) {
        setIsVerifying(false);
        setIsTokenValid(true);
        return;
      }

      setIsVerifying(true);
      setVerificationError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/accounts/me/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenToVerify}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setIsTokenValid(true);
          setVerificationError(null);
          setUserData(data);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setIsTokenValid(false);
          setVerificationError(
            errorData.detail || errorData.error || "Token is invalid or expired"
          );
          setUserData(null);
        }
      } catch (error) {
        console.error("Token verification error:", error);
        setIsTokenValid(false);
        setVerificationError(
          "Failed to verify token. Please try again or get a new link from the platform."
        );
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, []);


  const handleBack = () => {
    navigate(-1);
  };


  const getExpirationDate = () => {
    const date = new Date();
    if (isTrial) {
      date.setDate(date.getDate() + 7);
    } else if (isYearly) {
      date.setFullYear(date.getFullYear() + 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };


  const getCurrentMonth = () => {
    return new Date().toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const handleApplyPromo = () => {
    // Minimal promo handling so UI behaves sensibly
    if (!promoCode || promoCode.trim() === "") {
      setError("Please enter a promo code.");
      return;
    }
    const code = promoCode.trim().toLowerCase();
    if (code === "demo10") {
      setDiscount(10);
      setError(null);
    } else {
      setError("Invalid promo code.");
      setDiscount(0);
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!selectedPlanId) {
        setError("Please select a plan before continuing.");
        setLoading(false);
        return;
      }

      const requestBody = {
        plan_id: selectedPlanId,
        success_url: `${window.location.origin}/?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/?checkout=cancel`,
      };
      const checkoutToken = searchParams.get("token") || localStorage.getItem("token");


      // Call the API
      const response = await fetch(
        `${API_BASE_URL}/api/payments/subscription/create-checkout/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${checkoutToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          errorData.detail ||
          "Failed to create checkout session"
        );
      }

      const data = await response.json();

      if (data.checkout_url) {
        if (data.session_id) {
          localStorage.setItem("checkout_session_id", data.session_id);
        }

        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL received from server");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setError(
        error.message || "An error occurred while creating checkout session"
      );
      setLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isTokenValid && verificationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
              Authentication Failed
            </h2>
            <p className="text-gray-600 text-center mb-6">
              {verificationError}
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800 text-center">
                Link Expired. Please get a new link from the platform you were redirected from.
              </p>
            </div>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {(searchParams.get("token") || localStorage.getItem("token")) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <span className="font-semibold">Token-based checkout:</span> You're
            checking out with a secure token.
            {userData?.username && (
              <span className="ml-2">
                (User: <span className="font-semibold">{userData.username}</span>)
              </span>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <span className="font-semibold">Error:</span> {error}
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={handleBack}
          disabled={loading}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200 disabled:opacity-50"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">Back to plans</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Section: Pick your plan */}
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-brand-dark mb-2">
              {monthlyPlan?.name ?? "Plan"}
            </h2>
            <p className="text-gray-600 mb-6">Pick your plan</p>

            {/* Billing Cycle Selection */}
            <div className={`grid gap-4 mb-6 ${yearlyPlan && parseFloat(yearlyPlan.price) > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {/* Monthly Plan Card */}
              <div
                className={`relative bg-white rounded-xl p-6 border-2 cursor-pointer transition-all ${billingCycle === "monthly"
                    ? "border-brand-cta shadow-lg"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                onClick={() => {
                  setBillingCycle("monthly");
                  if (monthlyPlan?.id) setSelectedPlanId(monthlyPlan.id);
                }}
              >
                {billingCycle === "monthly" && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-brand-cta rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-brand-dark mb-2">
                  {isTrial ? "1 Week Free Trial" : "Monthly"}
                </h3>
                <div className="mb-4">
                  {isLoadingPlans && !monthlyPlan ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                      <span className="text-gray-500">Loading...</span>
                    </div>
                  ) : isTrial ? (
                    <>
                      <span className="text-3xl font-black text-brand-dark">Free</span>
                      <p className="text-sm text-gray-500 mt-1">You'll be billed after the trial ends</p>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-black text-brand-dark">
                        ${monthlyPlan?.price ?? "—"}
                      </span>
                      <span className="text-gray-500 ml-2">per month</span>
                    </>
                  )}
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  {isTrial ? (
                    <>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        7-day full access
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        Credit card required
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        Cancel anytime before trial ends
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        Billed Monthly
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        Maximum 2 users
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                        Maximum 5 devices
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* Yearly Plan Card — only shown when a valid yearly price exists */}
              {yearlyPlan && parseFloat(yearlyPlan.price) > 0 && <div
                className={`relative bg-white rounded-xl p-6 border-2 cursor-pointer transition-all ${billingCycle === "yearly"
                    ? "border-brand-cta shadow-lg"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                onClick={() => {
                  setBillingCycle("yearly");
                  if (yearlyPlan?.id) setSelectedPlanId(yearlyPlan.id);
                }}
              >
                {billingCycle === "yearly" && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-brand-cta rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-brand-dark mb-2">
                  Yearly
                </h3>
                <div className="mb-4">
                  {isLoadingPlans && !yearlyPlan ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                      <span className="text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg text-gray-400 line-through">
                        ${yearlyPlan?.price ?? "—"}
                      </span>
                      <span className="text-3xl font-black text-brand-dark">
                        ${yearlyPlan?.price ? Math.round(Number(yearlyPlan.price) / 12) : "—"}
                      </span>
                      <span className="text-gray-500">per month</span>
                    </div>
                  )}
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    Billed Annually
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    Maximum 2 users
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    Maximum 5 devices
                  </li>
                </ul>
              </div>}
            </div>
          </div>

          {/* Right Section: Summary */}
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-brand-dark mb-6">
              Summary
            </h2>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              {/* Order Details */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-brand-dark">
                      {isTrial ? "7-Day Trial" : billingCycle === "monthly" ? "Monthly" : "Yearly"} Pack : {" "}
                      {getCurrentMonth()}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      AnalystBro {monthlyPlan?.name ?? "Plan"} Access
                    </div>
                  </div>
                  <div className="text-right">
                    {isLoadingPlans && ((isYearly && !yearlyPlan) || (!isYearly && !monthlyPlan)) ? (
                      <div className="flex items-center gap-2 justify-end">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span className="text-gray-500 text-sm">Loading...</span>
                      </div>
                    ) : (
                      <div className="font-semibold text-brand-dark">
                        {isTrial ? "Free" : `$${isYearly ? (yearlyPlan?.price ?? "—") : (monthlyPlan?.price ?? "—")}`}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">{isTrial ? "Trial Ends On" : "Expires On"}</div>
                  <div className="text-sm font-medium text-brand-dark">
                    {getExpirationDate()}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">Auto Renewal</div>
                  <div className="text-sm font-medium text-brand-dark">
                    {isTrial ? "After trial ends" : "Enabled"}
                  </div>
                </div>

                {isTrial && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Your card will be charged once the 7-day trial period ends. Cancel anytime before <strong>{getExpirationDate()}</strong> to avoid being billed.
                    </p>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">Discount Applied</div>
                  <div className="text-sm font-medium text-brand-dark">
                    {discount > 0 ? `-$${discount}` : "None"}
                  </div>
                </div>
              </div>

              {/* Total Bill */}
              <div className="flex justify-between items-center py-4 border-t-2 border-gray-300 mb-6">
                <div className="text-lg font-bold text-brand-dark">
                  Total Bill
                </div>
                {isLoadingPlans && ((isYearly && !yearlyPlan) || (!isYearly && !monthlyPlan)) ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                    <span className="text-gray-500">Loading...</span>
                  </div>
                ) : (
                  <div className="text-2xl font-black text-brand-dark">
                    {isTrial ? "Free (7 days)" : `USD $${finalPrice}`}
                  </div>
                )}
              </div>

              {/* Apply Discount */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Apply promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-cta"
                  />
                  <button
                    onClick={handleApplyPromo}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleContinue}
                  disabled={loading}
                  className="w-full bg-gray-600 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    isTrial ? "Start Free Trial" : "Continue to billing"
                  )}
                </button>
                <button
                  onClick={handleBack}
                  disabled={loading}
                  className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
